'use client';

import Image from 'next/image';
import { Clip } from '@/lib/supabase';
import ClipViewer, { type ClipCreator } from '@/components/clips/ClipViewer';
import { useClipViewerUrl } from '@/components/clips/useClipViewerUrl';
import { ClipViewCount } from '@/components/clips/ClipViewCount';
import { useClipEngagement } from '@/components/clips/useClipEngagement';
import { ClipHoverPreview, useClipHoverPreview } from '@/components/clips/ClipHoverPreview';

/**
 * Interactive clips grid + full-screen viewer. Receives clips as props
 * (fetched on the server) so the thumbnails render into the initial HTML;
 * only the viewer open/navigate state lives on the client.
 */
export default function ProfileClips({ clips, creator }: { clips: Clip[]; creator: ClipCreator }) {
  const viewer = useClipViewerUrl(clips);
  const engagement = useClipEngagement(clips.map((c) => c.id));
  const hover = useClipHoverPreview();

  return (
    <>
      {clips.length > 0 ? (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5">
          {clips.map((clip, i) => (
            <button
              key={clip.id}
              type="button"
              onClick={() => viewer.open(i)}
              {...hover.bind(clip.id)}
              aria-label={`Play clip ${i + 1}`}
              className="relative aspect-[3/4] cursor-pointer bg-white/5 rounded-lg overflow-hidden group hover:bg-white/10 transition-colors"
            >
              {clip.thumbnail_url ? (
                <Image
                  src={clip.thumbnail_url}
                  alt={clip.title || 'Clip thumbnail'}
                  fill
                  sizes="(min-width: 1024px) 150px, (min-width: 640px) 25vw, 33vw"
                  className="object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect width="15" height="14" x="1" y="5" rx="2" ry="2" />
                  </svg>
                </div>
              )}
              {hover.hoveredId === clip.id && clip.video_url && <ClipHoverPreview src={clip.video_url} />}
              {engagement.available && <ClipViewCount count={engagement.countsFor(clip.id).view_count} />}
              <div className="absolute bottom-2 right-2 bg-black/60 rounded-full p-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="w-full h-32 flex flex-col items-center justify-center bg-white/5 rounded-lg border border-dashed border-white/10">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect width="15" height="14" x="1" y="5" rx="2" ry="2" />
          </svg>
          <p className="text-base text-gray-400 mt-2">No public clips</p>
        </div>
      )}

      <ClipViewer
        clips={clips}
        index={viewer.index}
        creator={creator}
        onIndexChange={viewer.go}
        onClose={viewer.close}
        source="profile"
        deepLinked={viewer.deepLinked}
      />
    </>
  );
}
